import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useRecordContext } from 'react-admin';
import JsonEditorAce from '../../components/code/ace/json.editor';

export const SchemaPreview: React.FC = () => {
  const record = useRecordContext();
  const [expandedPanel, setExpandedPanel] = useState<string | false>('properties');

  if (!record || !record.jsonSchema) {
    return (
      <Typography variant="body2" color="textSecondary">
        No schema definition available
      </Typography>
    );
  }

  let parsedSchema;
  try {
    parsedSchema = JSON.parse(record.jsonSchema);
  } catch (error) {
    return (
      <Typography variant="body2" color="error">
        Invalid JSON Schema format
      </Typography>
    );
  }

  const handlePanelChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const renderProperties = () => {
    if (!parsedSchema.properties) return null;

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Property</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Required</TableCell>
            <TableCell>Description</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(parsedSchema.properties).map(([key, prop]: [string, any]) => (
            <TableRow key={key}>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {key}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip 
                  label={prop.type || 'unknown'} 
                  size="small" 
                  variant="outlined"
                />
                {prop.format && (
                  <Chip 
                    label={prop.format} 
                    size="small" 
                    variant="outlined"
                    sx={{ ml: 0.5 }}
                  />
                )}
              </TableCell>
              <TableCell>
                {parsedSchema.required?.includes(key) ? (
                  <Chip label="Required" size="small" color="error" />
                ) : (
                  <Chip label="Optional" size="small" color="default" />
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {prop.description || '-'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Schema Definition
      </Typography>

      {/* Basic Info */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {parsedSchema.title || record.name}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          {parsedSchema.description || record.description}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label={`Type: ${parsedSchema.type}`} size="small" />
          <Chip label={`Version: ${record.currentVersion || '1.0.0'}`} size="small" />
          {parsedSchema.required?.length > 0 && (
            <Chip label={`${parsedSchema.required.length} required fields`} size="small" />
          )}
        </Box>
      </Paper>

      {/* Properties */}
      <Accordion 
        expanded={expandedPanel === 'properties'} 
        onChange={handlePanelChange('properties')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            Properties ({Object.keys(parsedSchema.properties || {}).length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {renderProperties()}
        </AccordionDetails>
      </Accordion>

      {/* Raw JSON Schema */}
      <Accordion 
        expanded={expandedPanel === 'raw'} 
        onChange={handlePanelChange('raw')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Raw JSON Schema</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ 
            height: '400px', 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden'
          }}>
            <JsonEditorAce
              value={JSON.stringify(parsedSchema, null, 2)}
              onChange={() => {}} // Read-only - no changes allowed
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
